import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import ListAltIcon from "@mui/icons-material/ListAlt";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import * as yup from "yup";
import FundsManagementService from "../services/FundsManagementService";
import Labels from "../labels/Labels";

export default function FundingAccounts() {
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [operationType, setOperationType] = useState(null); // 'load' or 'unload'
  const [dialogOpen, setDialogOpen] = useState(false);
  const [balanceDetailsOpen, setBalanceDetailsOpen] = useState(false);
  const [balanceDetailsAccount, setBalanceDetailsAccount] = useState(null);
  const [visibleGuids, setVisibleGuids] = useState({}); // Track which GUIDs are visible
  const [searchTerm, setSearchTerm] = useState("");
  const queryClient = useQueryClient();

  // Fetch funding accounts
  const { isLoading, isFetching, isError, data, error, refetch } = useQuery(
    ["fundingAccounts"],
    FundsManagementService.fetchFundingAccounts,
    {
      refetchOnWindowFocus: false,
      staleTime: 30000,
      retry: 1,
    }
  );

  const fundingAccounts = data?.data || [];
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const filteredFundingAccounts = fundingAccounts.filter((account) => {
    if (!normalizedSearchTerm) {
      return true;
    }

    const searchableValues = [
      account.accountGuid,
      account.accountType,
      account.status,
      ...(account.suffixes || []),
      ...(account.employerNames || []),
    ]
      .filter(Boolean)
      .map((value) => String(value).toLowerCase());

    return searchableValues.some((value) =>
      value.includes(normalizedSearchTerm)
    );
  });

  const isProgFundAccount = (account) =>
    String(account.accountType || "").toUpperCase() === "PROGFUND";

  const visibleFundingAccounts =
    filteredFundingAccounts.filter(isProgFundAccount);

  // Validation schema
  const validationSchema = yup.object({
    amount: yup
      .number()
      .typeError(
        Labels.getLabel("funds.amountMustBeNumber", "Amount must be a number")
      )
      .positive(
        Labels.getLabel("funds.amountMustBePositive", "Amount must be positive")
      )
      .required(Labels.getLabel("funds.amountRequired", "Amount is required")),
    description: yup
      .string()
      .required(
        Labels.getLabel("funds.descriptionRequired", "Description is required")
      ),
  });

  const formik = useFormik({
    initialValues: {
      amount: "",
      description: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      fundsMutation.mutate({
        accountGuid: selectedAccount.accountGuid,
        amount: parseFloat(values.amount),
        description: values.description,
        operation: operationType,
        wireDate: new Date().toISOString().split("T")[0],
      });
    },
  });

  // Mutation for load/unload funds
  const fundsMutation = useMutation(
    (data) => {
      if (data.operation === "load") {
        return FundsManagementService.loadFundsToAccount(data.accountGuid, {
          amount: data.amount,
          requestCode: "LT-WIRE_IN_ADJ",
          description: data.description,
          operation: "LOAD",
          wireDate: data.wireDate,
        });
      } else {
        return FundsManagementService.removeFundsFromAccount(data.accountGuid, {
          amount: data.amount,
          requestCode: "UT-WIRE_OUT_ADJ",
          description: data.description,
          operation: "UNLOAD",
          wireDate: data.wireDate,
        });
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["fundingAccounts"]);
        setDialogOpen(false);
        formik.resetForm();
        setSelectedAccount(null);
        setOperationType(null);
      },
    }
  );

  const handleLoadFunds = (account) => {
    setSelectedAccount(account);
    setOperationType("load");
    formik.resetForm();
    setDialogOpen(true);
  };

  const handleUnloadFunds = (account) => {
    setSelectedAccount(account);
    setOperationType("unload");
    formik.resetForm();
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedAccount(null);
    setOperationType(null);
    formik.resetForm();
  };

  const handleOpenBalanceDetails = (account) => {
    setBalanceDetailsAccount(account);
    setBalanceDetailsOpen(true);
  };

  const handleCloseBalanceDetails = () => {
    setBalanceDetailsOpen(false);
    setBalanceDetailsAccount(null);
  };

  const handleAmountChange = (event) => {
    const input = event.target.value;
    const formattedInput = input.replace(/[^0-9.]/g, "");
    if (/^\d*(\.\d{0,2})?$/.test(formattedInput)) {
      formik.setFieldValue("amount", formattedInput);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  };

  const getBalanceCompanySummary = (account) => {
    const groups = (account.balanceBreakdown || []).reduce(
      (result, balance) => {
        const parentId = balance.parentCompanyId || balance.companyId;
        const parentName =
          balance.parentCompanyName || balance.companyName || parentId;

        if (!result[parentId]) {
          result[parentId] = { parentName, companyNames: [] };
        }
        if (
          balance.companyName &&
          !result[parentId].companyNames.includes(balance.companyName)
        ) {
          result[parentId].companyNames.push(balance.companyName);
        }
        return result;
      },
      {}
    );

    return Object.values(groups).map((group) => {
      const childNames = group.companyNames.filter(
        (companyName) => companyName !== group.parentName
      );
      return childNames.length > 0
        ? `${group.parentName}: ${childNames.join(", ")}`
        : group.parentName;
    });
  };

  const handleCopyGuid = (guid) => {
    navigator.clipboard.writeText(guid).then(() => {
      console.log("GUID copied to clipboard:", guid);
    });
  };

  const toggleGuidVisibility = (guid) => {
    setVisibleGuids((prev) => ({
      ...prev,
      [guid]: !prev[guid],
    }));
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5">Funding Accounts</Typography>
        <IconButton
          onClick={() => refetch()}
          size="small"
          title="Refresh"
          disabled={isFetching}
        >
          {isFetching ? <CircularProgress size={18} /> : <RefreshIcon />}
        </IconButton>
      </Box>

      <TextField
        fullWidth
        size="small"
        label="Search funding accounts"
        placeholder="Search by account GUID, type, product, employer, or status"
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
        sx={{ mb: 2 }}
      />

      {isLoading && <CircularProgress />}
      {isError && (
        <Alert severity="error">
          Error loading funding accounts: {error?.message || "Unknown error"}
        </Alert>
      )}

      {!isLoading && visibleFundingAccounts.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell>Account GUID</TableCell>
                <TableCell>Products</TableCell>
                <TableCell>Companies</TableCell>
                <TableCell align="right">Available Balance</TableCell>
                <TableCell align="right">Ledger Balance</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleFundingAccounts.map((account, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() =>
                          toggleGuidVisibility(account.accountGuid)
                        }
                        title="Toggle GUID visibility"
                        sx={{ padding: "2px", minWidth: "24px" }}
                      >
                        {visibleGuids[account.accountGuid] ? (
                          <VisibilityOffIcon sx={{ fontSize: "1rem" }} />
                        ) : (
                          <VisibilityIcon sx={{ fontSize: "1rem" }} />
                        )}
                      </IconButton>
                      {visibleGuids[account.accountGuid] && (
                        <span
                          style={{
                            fontFamily: "monospace",
                            fontSize: "0.7rem",
                            userSelect: "all",
                          }}
                        >
                          {account.accountGuid}
                        </span>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleCopyGuid(account.accountGuid)}
                        title="Copy GUID"
                        sx={{ padding: "2px", minWidth: "24px" }}
                      >
                        <FileCopyIcon sx={{ fontSize: "0.9rem" }} />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {[...new Set(account.suffixes || [])]
                        .filter((product) => product !== "WIRE_OUT")
                        .map((product) => (
                          <Chip key={product} label={product} size="small" />
                        ))}
                      {[...new Set(account.suffixes || [])].filter(
                        (product) => product !== "WIRE_OUT"
                      ).length === 0 && "N/A"}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const companySummary = getBalanceCompanySummary(account);
                      const summaryText = companySummary.join(" | ");

                      return (
                        <Typography
                          variant="body2"
                          title={summaryText || "N/A"}
                          sx={{
                            fontSize: "1rem",
                            lineHeight: 1.25,
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: 2,
                            overflow: "hidden",
                            wordBreak: "break-word",
                          }}
                        >
                          {summaryText || "N/A"}
                        </Typography>
                      );
                    })()}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(account.availableBalance || 0)}
                  </TableCell>
                  <TableCell align="right">
                    <Box
                      sx={{
                        display: "flex",
                        width: "100%",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: 0.25,
                      }}
                    >
                      {formatCurrency(account.ledgerBalance || 0)}
                      {account.balanceBreakdown?.length > 0 && (
                        <IconButton
                          size="small"
                          onClick={() => handleOpenBalanceDetails(account)}
                          title="View balance details"
                          aria-label="View balance details"
                          sx={{ p: 0.25 }}
                        >
                          <ListAltIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{account.status || "UNKNOWN"}</TableCell>
                  <TableCell align="center">
                    <>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        onClick={() => handleLoadFunds(account)}
                        sx={{
                          mr: 1,
                          minWidth: "36px",
                          width: "36px",
                          padding: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <AddIcon sx={{ fontSize: "1.2rem" }} />
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="error"
                        onClick={() => handleUnloadFunds(account)}
                        sx={{
                          minWidth: "36px",
                          width: "36px",
                          padding: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginTop: "0.25rem",
                        }}
                      >
                        <RemoveIcon sx={{ fontSize: "1.2rem" }} />
                      </Button>
                    </>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!isLoading && visibleFundingAccounts.length === 0 && (
        <Alert severity="info">
          {filteredFundingAccounts.length > 0
            ? "No PROGFUND funding accounts available"
            : fundingAccounts.length === 0
              ? "No funding accounts found"
              : "No funding accounts match your search"}
        </Alert>
      )}

      <Dialog
        open={balanceDetailsOpen}
        onClose={handleCloseBalanceDetails}
        maxWidth="sm"
        fullWidth
      >
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" sx={{ color: "white" }}>
            Ledger Balance Details
          </Typography>
          <IconButton
            onClick={handleCloseBalanceDetails}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent>
          {(() => {
            const balanceGroups = (
              balanceDetailsAccount?.balanceBreakdown || []
            ).reduce((groups, balance) => {
              const parentId = balance.parentCompanyId || balance.companyId;
              const parentName =
                balance.parentCompanyName || balance.companyName || parentId;

              if (!groups[parentId]) {
                groups[parentId] = { parentName, balances: [] };
              }
              groups[parentId].balances.push(balance);
              return groups;
            }, {});

            return (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Company</TableCell>
                    <TableCell align="right">Balance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(balanceGroups).map(([parentId, group]) => (
                    <React.Fragment key={parentId}>
                      <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableCell colSpan={2}>
                          <strong>{group.parentName}</strong>
                        </TableCell>
                      </TableRow>
                      {group.balances.map((balance) => (
                        <TableRow key={balance.companyId}>
                          <TableCell sx={{ pl: 4 }}>
                            {balance.companyName}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(balance.balance || 0)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                  {Object.keys(balanceGroups).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        No positive ledger balances found
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell>
                      <strong>Total</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>
                        {formatCurrency(
                          balanceDetailsAccount?.ledgerBalance || 0
                        )}
                      </strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Fund Operation Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" sx={{ color: "white" }}>
            {operationType === "load" ? "Load Funds" : "Unload Funds"} to
            Account
          </Typography>
          <IconButton onClick={handleCloseDialog} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3 }}>
          {fundsMutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {fundsMutation.error?.response?.data?.message ||
                fundsMutation.error?.message ||
                "Operation failed"}
            </Alert>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary">
              Account: <strong>{selectedAccount?.accountGuid}</strong>
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Current Available Balance:{" "}
              <strong>
                {formatCurrency(selectedAccount?.availableBalance || 0)}
              </strong>
            </Typography>
          </Box>

          <form onSubmit={formik.handleSubmit}>
            <TextField
              fullWidth
              label="Amount"
              name="amount"
              value={formik.values.amount}
              onChange={handleAmountChange}
              error={formik.touched.amount && Boolean(formik.errors.amount)}
              helperText={formik.touched.amount && formik.errors.amount}
              placeholder="0.00"
              sx={{ mb: 2 }}
              inputProps={{ step: "0.01", min: "0" }}
            />

            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.description && Boolean(formik.errors.description)
              }
              helperText={
                formik.touched.description && formik.errors.description
              }
              placeholder="Enter description"
              sx={{ mb: 3 }}
            />

            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button
                variant="contained"
                color={operationType === "load" ? "success" : "error"}
                type="submit"
                disabled={fundsMutation.isLoading}
              >
                {fundsMutation.isLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  "Submit"
                )}
              </Button>
            </Box>
          </form>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
